import { createListenerMiddleware } from '@reduxjs/toolkit';
import { fileCheckStatusApi, type CheckFileStatusResponse } from '@/api/file/checkFileStatus';
import { createChatApi } from '@/api/chat/createChat';
import { clearChat, setChatId, setPendingUrlSyncChatId } from '@/store';
import {
  clearDeferredSendForChat,
  clearPendingMessageForChat,
  promoteSessionDocuments,
  setDeferredSendForChat,
  setDeferredSendErrorForChat,
  setPendingMessageForChat,
} from '@/store/slices/chat/chat';
import { streamSendChatMessage } from '@/services/chatStreamingSend';
import { ERROR_STATUS_VALUES, FileStatus } from '@/types/file';
import type { RootState } from '@/store/store';
import type { AppDispatch } from '@/store/store';
import { draftChat } from '@/constants';
import { selectUserAboutData } from '@/store/slices/users/userAbout';
import { globalToastService } from '@repo/atomic-components';
import { chatMessagesCache } from '@/hooks/chatMessagesCache';
import { isBrowserPathShowingChatId } from '@/utils/chat';
import { createProcessingStallTimer } from './processingStallTimer';

export const chatListenerMiddleware = createListenerMiddleware();

chatListenerMiddleware.startListening({
  actionCreator: setChatId,
  effect: (action) => {
    if (!action.payload?.chatId) return;

    // Store per-tab so opening a new browser window does not reuse an old chat.
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('chatId', action.payload.chatId);
  },
});

chatListenerMiddleware.startListening({
  actionCreator: clearChat,
  effect: () => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('chatId');
  },
});

const POLL_INTERVAL_MS = 5000;
// A large batch can take a long time, so give up only when no document status has changed for this long.
const MAX_PROCESSING_STALL_MS = 15 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 6;
const inFlightDeferred = new Set<string>();

chatListenerMiddleware.startListening({
  actionCreator: setDeferredSendForChat,
  effect: async (action, listenerApi) => {
    const { chatId } = action.payload;
    if (!chatId) return;

    if (inFlightDeferred.has(chatId)) return;
    inFlightDeferred.add(chatId);

    const isOpenChat = (id: string) => (listenerApi.getState() as RootState).chat.chatId === id;

    // Gives up on the send: the prompt goes back into the input and the user is told why.
    const failDeferredSend = (reason: string, toastDetail: string) => {
      const entry = (listenerApi.getState() as RootState).chat.deferredSends[chatId];
      listenerApi.dispatch(setDeferredSendErrorForChat({ chatId, prompt: entry?.prompt ?? '', reason }));
      listenerApi.dispatch(clearDeferredSendForChat({ chatId }));
      globalToastService.show({ closable: true, severity: 'error', detail: toastDetail, life: 6000 });
    };

    try {
      const dispatch = listenerApi.dispatch as AppDispatch;
      const stallTimer = createProcessingStallTimer(MAX_PROCESSING_STALL_MS);
      let consecutivePollFailures = 0;

      // Poll until documents are READY_FOR_CHAT, then send message in the background.
      while (true) {
        if (stallTimer.isStalled()) {
          failDeferredSend(
            'Timed out waiting for files to finish processing.',
            'Your files are taking too long to process, so your message was not sent. Please try sending it again.',
          );
          return;
        }

        const state = listenerApi.getState() as RootState;
        const entry = state.chat.deferredSends[chatId];

        if (!entry) {
          return;
        }

        // An empty deferred entry should never be considered "ready".
        if (entry.documentIds.length === 0) {
          listenerApi.dispatch(
            setDeferredSendErrorForChat({
              chatId,
              prompt: entry.prompt,
              reason: 'No documents were queued for deferred send.',
            }),
          );
          listenerApi.dispatch(clearDeferredSendForChat({ chatId }));
          return;
        }

        const poll = dispatch(
          fileCheckStatusApi.endpoints.checkFileStatus.initiate(
            { fletchDocumentIds: entry.documentIds },
            { forceRefetch: true, subscribe: false },
          ),
        );
        let res: CheckFileStatusResponse;
        try {
          res = await poll.unwrap();
          consecutivePollFailures = 0;
        } catch {
          consecutivePollFailures += 1;
          if (consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
            failDeferredSend(
              'Could not check file processing status.',
              'We could not check whether your files are ready, so your message was not sent. Please try again.',
            );
            return;
          }
          await listenerApi.delay(POLL_INTERVAL_MS);
          continue;
        }

        const statusItems = entry.documentIds.map((id) => res.documentStatuses?.[id]);
        const statuses = statusItems.map((s) => s?.status).filter(Boolean) as string[];
        stallTimer.record(statusItems.map((s) => s?.status));

        const hasValidationError = statusItems.some((s) => Boolean(s?.validationError));
        const hasFailedStatus = statuses.some((s) => ERROR_STATUS_VALUES.includes(s as FileStatus));

        if (hasValidationError || hasFailedStatus) {
          listenerApi.dispatch(
            setDeferredSendErrorForChat({
              chatId,
              prompt: entry.prompt,
              reason: hasValidationError ? 'Validation error while processing files.' : 'File processing failed.',
            }),
          );
          listenerApi.dispatch(clearDeferredSendForChat({ chatId }));
          // The open chat shows the failed file; from another page nothing else would.
          if (!isOpenChat(chatId)) {
            globalToastService.show({
              closable: true,
              severity: 'error',
              detail: 'A file failed to process, so your message was not sent. Please try sending it again.',
              life: 6000,
            });
          }
          return;
        }

        const allReady =
          statuses.length === entry.documentIds.length && statuses.every((s) => s === FileStatus.READY_FOR_CHAT);

        if (allReady) {
          let ensuredChatId = chatId;
          if (chatId === draftChat && entry.createChat) {
            const userId = selectUserAboutData(listenerApi.getState() as RootState)?.userId;

            if (!userId) {
              listenerApi.dispatch(
                setDeferredSendErrorForChat({
                  chatId,
                  prompt: entry.prompt,
                  reason: 'Could not determine user for chat creation.',
                }),
              );
              listenerApi.dispatch(clearDeferredSendForChat({ chatId }));
              globalToastService.show({
                closable: true,
                severity: 'error',
                detail: 'Something went wrong. Please try again.',
                life: 6000,
              });
              return;
            }

            const created = await dispatch(
              createChatApi.endpoints.postCreateChat.initiate({ userId, matterId: entry.caseId }),
            ).unwrap();
            ensuredChatId = created.chatId;

            // Promote draft-keyed UI state so the Chat view updates immediately.
            listenerApi.dispatch(setChatId({ chatId: created.chatId }));
            chatMessagesCache.migrate(draftChat, created.chatId);
            const latestState = listenerApi.getState() as RootState;
            const pendingDraft = latestState.chat.pendingMessages[draftChat];
            if (pendingDraft) {
              listenerApi.dispatch(
                setPendingMessageForChat({
                  chatId: created.chatId,
                  userPrompt: pendingDraft.userPrompt,
                  attachedDocuments: pendingDraft.attachedDocuments,
                  isStreaming: pendingDraft.isStreaming,
                }),
              );
              listenerApi.dispatch(clearPendingMessageForChat({ chatId: draftChat }));
            }
          }

          // The user may have left for another page or chat; its URL and files aren't this send's.
          if (isOpenChat(ensuredChatId)) {
            if (!isBrowserPathShowingChatId(ensuredChatId)) {
              listenerApi.dispatch(setPendingUrlSyncChatId({ chatId: ensuredChatId }));
            }

            // Same ordering as `useChat` handleSend: promote before streaming so the input strip does
            // not briefly show session attachments when `isLoading` flips false in `finally` mid-await.
            listenerApi.dispatch(promoteSessionDocuments());
          }

          // Fire the actual send and clear deferred state.
          await streamSendChatMessage({
            dispatch,
            chatId: ensuredChatId,
            content: entry.prompt,
            options: {
              existingDocumentIds: entry.existingDocumentIds ?? [],
              newlySelectedHistoryRowIds: entry.selectedHistoryRowIds ?? [],
              messageAttachmentDocuments: entry.messageAttachmentDocuments,
              messageType: entry.messageType ?? 'USER_MESSAGE',
            },
          });

          // Clear deferred + any draft pending message if we created a chat from draft key.
          listenerApi.dispatch(clearDeferredSendForChat({ chatId }));
          if (chatId === draftChat) {
            listenerApi.dispatch(clearPendingMessageForChat({ chatId: draftChat }));
          }
          return;
        }

        await listenerApi.delay(POLL_INTERVAL_MS);
      }
    } finally {
      inFlightDeferred.delete(chatId);
    }
  },
});
